// src/lib/format-time.ts
//
// Refinement #13 — generalized relative dates. Used everywhere: card
// rows, table columns, card drawer, activity feed, comments, messages,
// git panel. One place to maintain the logic + one consistent voice.
//
// Rules:
//   < 60s    → "just now"
//   < 60m    → "Xm ago"
//   < 24h    → "Xh ago"
//   < 48h    → "yesterday"
//   < 7d     → "Xd ago"
//   < 28d    → "Xw ago"
//   same year → "Mon DD"   (e.g. "Apr 11")
//   else     → "Mon DD, YYYY"

/**
 * Format a timestamp as a human-readable relative or absolute string.
 * Accepts ISO 8601 strings, Unix epoch seconds, or Date objects.
 */
export function timeAgo(input: string | number | Date | null | undefined): string {
  if (input == null) return '—';
  const d = typeof input === 'number'
    ? new Date(input < 1e12 ? input * 1000 : input)  // epoch seconds vs ms
    : new Date(input);
  if (isNaN(d.getTime())) return '—';

  const now  = Date.now();
  const diff = now - d.getTime();
  const sec  = Math.floor(diff / 1000);

  if (sec < 60)     return 'just now';
  if (sec < 3600)   return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400)  return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 172800) return 'yesterday';
  const days = Math.floor(sec / 86400);
  if (days < 7)  return `${days}d ago`;
  if (days < 28) return `${Math.floor(days / 7)}w ago`;

  const thisYear = new Date().getFullYear();
  if (d.getFullYear() === thisYear) {
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Format a date as a short absolute string: "Mon DD" this year,
 * "Mon DD, YYYY" otherwise. Used where you always want the date,
 * not a relative offset (e.g. "Created: Apr 11").
 */
export function shortDate(input: string | number | Date | null | undefined): string {
  if (input == null) return '—';
  const d = new Date(input);
  if (isNaN(d.getTime())) return '—';
  const thisYear = new Date().getFullYear();
  if (d.getFullYear() === thisYear) {
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Due-date urgency classification (refinement #14).
 *
 *   overdue   → past due
 *   today     → due today
 *   soon      → ≤ 2 days
 *   upcoming  → ≤ 5 days
 *   normal    → > 5 days or no due date
 *
 * Used by CardItem, TableView, and the card drawer to pick a color
 * ramp for the due-date chip.
 */
export type DueUrgency = 'overdue' | 'today' | 'soon' | 'upcoming' | 'normal';

export function dueUrgency(dueDate: string | null | undefined): DueUrgency {
  if (!dueDate) return 'normal';
  const due = new Date(dueDate);
  if (isNaN(due.getTime())) return 'normal';

  const now   = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const d     = new Date(due.getFullYear(), due.getMonth(), due.getDate());

  const diffMs = d.getTime() - today.getTime();
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffDays < 0)  return 'overdue';
  if (diffDays === 0) return 'today';
  if (diffDays <= 2)  return 'soon';
  if (diffDays <= 5)  return 'upcoming';
  return 'normal';
}

/**
 * OKLCH color for each urgency tier.
 */
export const DUE_URGENCY_COLORS: Record<DueUrgency, string> = {
  overdue:  'oklch(0.62 0.26 25)',      // red
  today:    'oklch(0.68 0.24 25)',      // red-orange
  soon:     'oklch(0.78 0.16 85)',      // gold/orange
  upcoming: 'oklch(0.72 0.08 85)',      // faded gold
  normal:   'oklch(0.55 0.04 310 / 0.5)', // neutral muted
};

/**
 * Format a due date with its urgency prefix.
 * "Apr 12" (normal) / "⚠ Apr 12" (overdue) / "Apr 12" (today, bold in CSS).
 */
export function formatDueDate(dueDate: string | null | undefined): string {
  if (!dueDate) return '—';
  const d = new Date(dueDate);
  if (isNaN(d.getTime())) return '—';
  const urgency = dueUrgency(dueDate);
  const label = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  if (urgency === 'overdue') return `⚠ ${label}`;
  return label;
}
